import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { Controller, useForm } from 'react-hook-form';
import { selectSelectedDevice } from '../../../redux/app/platformSlice';
import { getDeviceIdForAppiumScripts } from '../../../utilities/getDevideIdForAppiumScripts';

export default function RunSummaryExportModal({
  open,
  onClose,
  onExport,
  deviceOptions,
  filtersVersion,
  loading,
  error,
}) {
  const selectedDevice = useSelector(selectSelectedDevice);
  const form = useForm({
    defaultValues: {
      deviceId: '',
      appVersion: '',
      startDate: '',
      endDate: '',
    },
  });

  useEffect(() => {
    if (selectedDevice) {
      form.setValue('deviceId', getDeviceIdForAppiumScripts(selectedDevice));
    }
  }, [selectedDevice, form]);

  const allBuilds = React.useMemo(
    () => [...new Set([...(filtersVersion.android || []), ...(filtersVersion.ios || [])])].sort(),
    [filtersVersion],
  );

  const handleSubmit = form.handleSubmit((data) => {
    onExport(data);
  });

  const handleClose = () => {
    form.reset();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <Box component="form" onSubmit={handleSubmit}>
        <DialogTitle>Export Run Summary</DialogTitle>
        <DialogContent
          sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}
        >
          {error && <Alert severity="error">{error}</Alert>}

          <Controller
            name="deviceId"
            control={form.control}
            render={({ field }) => (
              <TextField
                {...field}
                select
                label="Device"
                size="small"
                InputLabelProps={{ shrink: true }}
                SelectProps={{
                  displayEmpty: true,
                  renderValue: (v) =>
                    v ? deviceOptions.find((d) => d.value === v)?.name || v : 'All Devices',
                }}
              >
                <MenuItem value="">All Devices</MenuItem>
                {deviceOptions.map((d) => (
                  <MenuItem key={d.id} value={d.value}>
                    {d.name}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />

          <Controller
            name="appVersion"
            control={form.control}
            render={({ field }) => (
              <TextField
                {...field}
                select
                label="App Build"
                size="small"
                InputLabelProps={{ shrink: true }}
                SelectProps={{ displayEmpty: true, renderValue: (v) => v || 'All Builds' }}
              >
                <MenuItem value="">All Builds</MenuItem>
                {allBuilds.map((v) => (
                  <MenuItem key={v} value={v}>
                    {v}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />

          <TextField
            label="Start Date"
            type="datetime-local"
            size="small"
            InputLabelProps={{ shrink: true }}
            {...form.register('startDate')}
          />

          <TextField
            label="End Date"
            type="datetime-local"
            size="small"
            InputLabelProps={{ shrink: true }}
            {...form.register('endDate')}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={18} /> : <FileDownloadIcon />}
          >
            {loading ? 'Exporting…' : 'Export CSV'}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}
